

import React, { useState } from 'react';
import PageTitle from '../../components/title/pageTitle/PageTitle';
import { Pagination, Table, Tabs, Button, Tooltip, DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './Roster.scss';
import { getRosterColumns } from './RosterHelper';
import { useRosterController } from './useRosterController';
import DeleteConfirmationModal from './DeleteConfirmationModal';

dayjs.extend(customParseFormat);


const Roster = () => {
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    dataSource,
    setDataSource,
    loading,
    totalResults,
    viewMode,
    setViewMode,
    planMonth,
    setPlanMonth,
    addMonth,
    setAddMonth,
    editingKey,
    setEditingKey,
    setOriginalRecord,
    originalRecord,
    handleCellChange,
    handleBulkAdd,
    handleSaveRoster,
    handleEdit,
    handleCancel,
    onShowSizeChange,
    isBulkAddDisabled,
    handleDelete,
    selectedRowKeys,
    setSelectedRowKeys
  } = useRosterController();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteIds, setDeleteIds] = useState<(string | number)[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteClick = (ids: (string | number)[]) => {
    setDeleteIds(ids);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    await handleDelete(deleteIds);
    setDeleteLoading(false);
    setOpenDeleteModal(false);
    setDeleteIds([]);
  };

  const activeMonth = viewMode === 'plan' ? planMonth : addMonth;
  const isCurrentMonth = activeMonth === dayjs().format('MM-YYYY');

  const columns = getRosterColumns(handleCellChange, handleEdit, handleSaveRoster, handleCancel, handleDeleteClick, editingKey, viewMode, originalRecord, isCurrentMonth);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    fixed: true,
  };

  const rosterTableContent = (
    <>
      <Table
        rowSelection={viewMode === 'plan' ? rowSelection : undefined}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        scroll={{ y: 550 }}
        className="roster-table"
      />
      {totalResults > 0 && (
        <div className='pagination mt-auto pt-5'>
          <Pagination
            className='float-right flex flex-row p-6'
            showSizeChanger
            onShowSizeChange={onShowSizeChange}
            current={currentPage}
            total={totalResults}
            pageSize={pageSize}
            onChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}
    </>
  );

  return (
    <div className='page-container bg-customBg'>
      <div className="full-width-container">
        <PageTitle title="Roster Management" />
        <div className='bg-white tab-container p-4 rounded-lg flex flex-col roster-page'>
          <Tabs
            activeKey={viewMode}
            onChange={(key: string) => {
              setViewMode(key as 'plan' | 'add');
              setCurrentPage(1);
              setPageSize(10);
              setEditingKey('');
              setOriginalRecord(null);
              setDataSource([]);
              setSelectedRowKeys([]);
            }}
            tabBarExtraContent={
              <div className="flex items-center gap-4">
                <DatePicker
                  picker="month"
                  format="MM-YYYY"
                  value={viewMode === 'plan' ? (planMonth ? dayjs(planMonth, 'MM-YYYY') : null) : (addMonth ? dayjs(addMonth, 'MM-YYYY') : null)}
                  onChange={(date) => {
                    if (date) {
                      const newMonth = date.format('MM-YYYY');
                      if (viewMode === 'plan') {
                        setPlanMonth(newMonth);
                      } else {
                        setAddMonth(newMonth);
                      }
                    }
                  }}
                  disabledDate={(current) => {
                    const today = dayjs();

                    if (viewMode === 'plan') {
                      return current && current > today.endOf('month');
                    }
                    const endOfMonth = today.endOf('month');
                    const daysRemaining = endOfMonth.diff(today, 'day');

                    const isCurrentMonth = current && current.isSame(today, 'month');
                    const isNextMonth = current && current.isSame(today.add(1, 'month'), 'month');

                    if (isCurrentMonth) return false;
                    if (isNextMonth && daysRemaining < 3) return false;

                    return true;
                  }}
                  allowClear={false}
                  className="w-40"
                />
                {viewMode === 'add' ? (
                  <Button
                    type="primary"
                    className="addButton"
                    size="large"
                    onClick={handleBulkAdd}
                    disabled={isBulkAddDisabled || !isCurrentMonth}
                  >
                    Submit
                  </Button>
                ) : viewMode === 'plan' && selectedRowKeys.length > 0 ? (
                  <Tooltip title='Delete'>
                    <Button
                      type="primary"
                      shape="circle"
                      size="large"
                      className="flex items-center justify-center mr-4"
                      onClick={() => handleDeleteClick(selectedRowKeys as number[])}
                      disabled={!isCurrentMonth}
                    >
                      <span className="material-symbols-outlined">
                        delete
                      </span>
                    </Button>
                  </Tooltip>
                ) : null}
              </div>
            }
            items={[
              {
                key: 'plan',
                label: 'Roster Plan',
                children: rosterTableContent,
              },
              {
                key: 'add',
                label: 'Add Roster Plan',
                children: rosterTableContent,
              }
            ]}
          />
        </div>
      </div>
      <DeleteConfirmationModal
        open={openDeleteModal}
        handleCloseDelete={() => setOpenDeleteModal(false)}
        handleDelete={confirmDelete}
        loading={deleteLoading}
        deleteMessage={deleteIds.length > 1
          ? `Are you sure you want to delete these ${deleteIds.length} roster plans?`
          : 'Are you sure you want to delete this roster plan?'}
      />
    </div>
  );
};

export default Roster;